
import React from "react";
import { ExternalLink, Globe, Mail, Phone, FileText, Info } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const NadPage = () => {
  return (
    <>
    <Header />
    <div className="w-full bg-white">

      {/* Full Width Hero Section */}
      <section className="relative w-full h-[200px] md:h-[240px] overflow-hidden">

        <img
          src="/images/campusKumaun.avif"
          alt="Kumaun University"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#d1fae5]/95 via-[#a7f3d0]/80 to-[#ecfdf5]/40" />
        <div className="relative z-10 max-w-7xl mx-auto h-full px-6 md:px-10 flex items-center">
          <div>
            <div className="flex flex-wrap items-center gap-2 text-sm md:text-base text-[#123B5D]  font-gill mb-5 font-gill">
              <span className="text-[#123B5D] text-lg font-gill">Home</span>
              <span className="text-[#123B5D] text-lg font-gill">/</span>
              <span className="font-semibold text-[#123B5D] text-lg font-gill">
                 National Academic Depository
              </span>
            </div>

            <div className="w-16 h-1 bg-[#B5121B] mb-5 rounded-full" />

            <h1 className="text-3xl md:text-5xl font-bold text-[#064E3B] tracking-wide">
              NATIONAL ACADEMIC DEPOSITORY
            </h1>

          
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-12">

        {/* Introduction */}
        <div className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-[#08245c] mb-3">
            National Academic Depository
          </h2>

          <div className="w-20 h-1 bg-[#B5121B] rounded-full mb-5" />

          <p className="text-gray-600 text-base md:text-lg leading-8 text-justify">
           Please follow the following link for login onto the NAD portal-
          </p>
        </div>

        {/* Portal Login Links */}
        <div className="bg-[#f5f8fc] border border-gray-200 rounded-2xl p-6 md:p-8 mb-10">

          <h3 className="text-xl md:text-2xl font-bold text-[#08245c] mb-6">
            NAD Portal Login
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            <a
              href="https://www.digilocker.gov.in"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-4 bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-[#B5121B] transition-all duration-300 group"
            >
              <div className="flex items-center gap-4">
                <div className="bg-blue-50 p-3 rounded-xl">
                  <Globe className="text-[#08245c] w-7 h-7" />
                </div>

                <div>
                  <h4 className="font-bold text-gray-800 text-lg">
                    DigiLocker Portal
                  </h4>
                  <p className="text-sm text-gray-500">
                    Access Academic Documents
                  </p>
                </div>
              </div>

              <ExternalLink className="text-gray-400 group-hover:text-[#B5121B]" />
            </a>

            <a
              href="https://nad.gov.in"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-4 bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-[#B5121B] transition-all duration-300 group"
            >
              <div className="flex items-center gap-4">
                <div className="bg-blue-50 p-3 rounded-xl">
                  <Globe className="text-[#08245c] w-7 h-7" />
                </div>

                <div>
                  <h4 className="font-bold text-gray-800 text-lg">
                    NAD Portal
                  </h4>
                  <p className="text-sm text-gray-500">
                    National Academic Depository
                  </p>
                </div>
              </div>

              <ExternalLink className="text-gray-400 group-hover:text-[#B5121B]" />
            </a>

          </div>
        </div>

        {/* Instructions */}
        <div className="mb-10">

          <h3 className="text-xl md:text-2xl font-bold text-[#08245c] mb-6 flex items-center gap-3">
            <FileText className="text-[#B5121B]" />
            Steps 
          </h3>

          <div className="border border-gray-200 rounded-2xl p-5 md:p-8 bg-white shadow-sm">

            <ol className="space-y-5">

              {[
                "Login using your registered mobile number and OTP.",
                "Enter your Name (as per Aadhaar).",
                "Enter your Roll Number.",
                "Enter your Registration Number. To fetch the document, try both formats with and without the KU prefix (e.g. KUXXXXXXXXX or XXXXXXXXX).",
                "Select the Year.",
                "Click on Submit.",
                "Click on Get Document."
              ].map((step, index) => (
                <li key={index} className="flex items-start gap-4">

                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#08245c] text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </span>

                  <p className="text-gray-700 text-base md:text-lg leading-7 pt-0.5">
                    {step}
                  </p>

                </li>
              ))}

            </ol>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-5 md:p-7 mb-8">

          <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Info className="text-amber-600" />
            Important Note
          </h3>

          <p className="text-gray-700 leading-8 text-base md:text-lg text-justify">
            Students enrolled in{" "}
            <strong>NEP courses</strong> having{" "}
            <strong>APAAR ID (Automated Permanent Academic Account Registry)</strong>{" "}
            can only download their degree certificates and marksheets
            from 2022 onwards.
          </p>

        </div>

        {/* Support Information */}
        <div className="bg-[#08245c] rounded-2xl p-6 md:p-8 text-white">

          <h3 className="text-xl md:text-2xl font-bold mb-5">
            Need Help? Contact Support
          </h3>

          <p className="text-white/85 mb-5">
            If you still face any problems while accessing your
            academic documents, please contact the support team.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            <a
              href="tel:18008893511"
              className="flex items-center gap-3 hover:text-yellow-300 transition"
            >
              <Phone className="w-6 h-6" />
              <span>
                <strong>Helpline:</strong> 18008893511
              </span>
            </a>

            <a
              href="mailto:NAD@kunainital.ac.in"
              className="flex items-center gap-3 hover:text-yellow-300 transition"
            >
              <Mail className="w-6 h-6" />
              <span>
                <strong>Email:</strong> NAD@kunainital.ac.in
              </span>
            </a>

            <a
              href="https://support.abc.gov.in"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 hover:text-yellow-300 transition md:col-span-2"
            >
              <Globe className="w-6 h-6" />
              <span>
                <strong>Support Portal:</strong> support.abc.gov.in
              </span>
              <ExternalLink className="w-4 h-4" />
            </a>

          </div>
        </div>

      </section>
    </div>
    <Footer />
    </>
  );
};

export default NadPage;
